/**
 * The diner's checkout answers, turned into what paio-commerce accepts
 * (ORDR-166, against COMM-182 `services/handover.py` at `65aeac8`).
 *
 * THE TWO MODE LITERALS BELOW ARE COMMERCE'S RULE, NOT A MODE THIS APP SENDS.
 * Commerce takes a vehicle for `CURBSIDE` and only `CURBSIDE`, an address for
 * `DELIVERY` and only `DELIVERY`, comparing the cart's mode to those words. The
 * mode sent is always the resolver's (`noResolverLiterals.test.ts`); these only
 * decide which inputs to draw, and a mismatch is still refused by commerce with
 * a field error the Checkout shows.
 *
 * E.164 ONLY. Commerce will not guess a country. Nothing this app reads names
 * the brand's or location's country (measured: the flow, the directory, the
 * schedule and the brand read carry none), so the calling code is +1, shown
 * beside the input, and a diner may type another with its `+`.
 */

import type { FieldErrors, Handover } from "../customer/commerceClient.js";
import type { KitchenNote } from "../order/orderLine.js";

/** `services/handover.py` `MAX_KITCHEN_NOTE`. */
export const KITCHEN_NOTE_MAX = 280;

/** What the phone input shows in front of the number. */
export const CALLING_CODE = "1";

const VEHICLE_MODE = "CURBSIDE";
const ADDRESS_MODE = "DELIVERY";

/** `services/handover.py` `PHONE_SEPARATORS` and `E164`. */
const SEPARATORS = /[\s\-.()]/g;
const E164 = /^\+[1-9][0-9]{7,14}$/;

export function carriesVehicle(mode: string): boolean {
  return mode === VEHICLE_MODE;
}

export function carriesAddress(mode: string): boolean {
  return mode === ADDRESS_MODE;
}

/**
 * The number in E.164, or `null` when it cannot be one. Ten local digits take
 * `+<callingCode>`; eleven starting with the calling code take a `+`.
 */
export function toE164(raw: string, callingCode: string): string | null {
  const compact = raw.replace(SEPARATORS, "");
  let candidate: string;
  if (compact.startsWith("+")) {
    candidate = compact;
  } else if (/^[0-9]{10}$/.test(compact)) {
    candidate = `+${callingCode}${compact}`;
  } else if (compact.length === 10 + callingCode.length && compact.startsWith(callingCode)) {
    candidate = `+${compact}`;
  } else {
    return null;
  }
  return E164.test(candidate) ? candidate : null;
}

export type ComposedNote = { readonly ok: true; readonly note: string | null } | { readonly ok: false; readonly message: string };

/** ONE `kitchen_note`: "<item name>: <note>" per noted line. Refused over 280, never cut. */
export function composeKitchenNote(notes: readonly KitchenNote[]): ComposedNote {
  if (notes.length === 0) {
    return { ok: true, note: null };
  }
  const note = notes.map((entry) => `${entry.name}: ${entry.note}`).join("\n");
  if (note.length > KITCHEN_NOTE_MAX) {
    return {
      ok: false,
      message: `Your kitchen notes come to ${String(note.length)} characters, and the kitchen takes at most ${String(KITCHEN_NOTE_MAX)}. Shorten a note in your cart, then place the order. Nothing has been charged.`,
    };
  }
  return { ok: true, note };
}

export interface ModeSources {
  /** The modes the location publishes hours for; `null` when that read has not answered. */
  readonly declared: readonly string[] | null;
  /** The flow's `fulfilment_mode`, or `null`. */
  readonly resolved: string | null;
  /** The flow's MODE step was SKIPPED: one mode, nothing to choose. */
  readonly settled: boolean;
}

/** The modes the picker offers. Never one the location does not declare. */
export function offeredModes({ declared, resolved, settled }: ModeSources): string[] {
  if (resolved !== null && (settled || declared === null)) {
    return [resolved];
  }
  return [...new Set(declared ?? [])];
}

/** What the diner typed at Checkout, verbatim. */
export interface Answers {
  readonly name: string;
  readonly phone: string;
  readonly car: string;
  readonly line1: string;
  readonly line2: string;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
}

/**
 * Which commerce field-error keys each input answers for. A key no input
 * answers for is shown above Place order, never dropped.
 */
export const FIELD_KEYS: Readonly<Record<keyof Answers, readonly string[]>> = {
  name: ["contact", "contact.name"],
  phone: ["contact.phone"],
  car: ["vehicle", "vehicle.description", "vehicle.colour", "vehicle.plate"],
  line1: ["delivery_address", "delivery_address.line1"],
  line2: ["delivery_address.line2"],
  city: ["delivery_address.city"],
  region: ["delivery_address.region"],
  postalCode: ["delivery_address.postal_code"],
};

export const NO_ANSWERS: Answers = { name: "", phone: "", car: "", line1: "", line2: "", city: "", region: "", postalCode: "" };

export type BuiltHandover =
  | { readonly ok: true; readonly handover: Handover }
  /** Keyed as commerce keys its own refusals, so one display serves both. */
  | { readonly ok: false; readonly fieldErrors: FieldErrors; readonly message: string | null };

const REQUIRED = ["Required."];

function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The handover commerce will be sent, or every reason it cannot be sent yet. */
export function buildHandover(mode: string | null, answers: Answers, notes: readonly KitchenNote[]): BuiltHandover {
  if (mode === null) {
    return { ok: false, fieldErrors: {}, message: "Choose how you want your order, then place it." };
  }
  const errors: Record<string, string[]> = {};
  const name = answers.name.trim();
  if (name === "") {
    errors["contact.name"] = REQUIRED;
  }
  const phone = toE164(answers.phone, CALLING_CODE);
  if (phone === null) {
    errors["contact.phone"] = ["Enter a 10-digit phone number, or one starting with + and its country code."];
  }
  const vehicle = carriesVehicle(mode) ? { description: optional(answers.car), colour: null, plate: null } : undefined;
  if (vehicle !== undefined && vehicle.description === null) {
    errors["vehicle.description"] = ["Tell us your car, so we can find you."];
  }
  const address = carriesAddress(mode)
    ? {
        line1: answers.line1.trim(),
        line2: optional(answers.line2),
        city: answers.city.trim(),
        region: optional(answers.region),
        postal_code: answers.postalCode.trim(),
      }
    : undefined;
  for (const part of ["line1", "city", "postal_code"] as const) {
    if (address !== undefined && address[part] === "") {
      errors[`delivery_address.${part}`] = REQUIRED;
    }
  }
  const note = composeKitchenNote(notes);
  if (Object.keys(errors).length > 0 || !note.ok || phone === null) {
    return { ok: false, fieldErrors: errors, message: note.ok ? null : note.message };
  }
  return {
    ok: true,
    handover: {
      contact: { name, phone },
      ...(vehicle === undefined ? {} : { vehicle }),
      ...(address === undefined ? {} : { delivery_address: address }),
      kitchen_note: note.note,
    },
  };
}
